import React from 'react';
import { Sparkles, Download, Copy, Share2, Rocket, MapPin, Clock, Briefcase, Wallet } from 'lucide-react';
import { formatSalary } from '../../lib/salary';

const toolBtn = (extra = {}) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.5rem 0.85rem', borderRadius: 999,
  fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', border: '1px solid #ECE7DE', background: '#fff', color: '#475569',
  transition: 'background-color 0.15s, color 0.15s, border-color 0.15s', ...extra,
});

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '1.4rem' }}>
    <div style={{ fontFamily: 'Cabinet Grotesk, sans-serif', fontSize: '0.8125rem', fontWeight: 700, color: '#0076A3', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.6rem' }}>{title}</div>
    {children}
  </div>
);

const Bullets = ({ text }) => (
  <ul style={{ margin: 0, paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
    {text.split('\n').map((l) => l.replace(/^[-•\s]+/, '').trim()).filter(Boolean).map((l, i) => (
      <li key={`${i}-${l}`} style={{ fontSize: '0.875rem', color: '#334155', lineHeight: 1.55 }}>{l}</li>
    ))}
  </ul>
);

const Chips = ({ items, color }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
    {items.map((s) => (
      <span key={s} style={{ padding: '4px 10px', borderRadius: 999, background: `${color}12`, border: `1px solid ${color}30`, fontSize: '0.75rem', color, fontWeight: 600 }}>{s}</span>
    ))}
  </div>
);

export function JDPreview({ jd, generating, creating, onGenerate, onDownload, onCopy, onShare, onCreate }) {
  const empty = !jd.title && !jd.overview && jd.requiredSkills.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '0.85rem 1.1rem', borderBottom: '1px solid #F1F5F9', background: '#fff', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'Cabinet Grotesk, sans-serif', fontSize: '0.9375rem', fontWeight: 700, color: '#002A38' }}>Live Preview</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" data-testid="generate-ai-button" onClick={onGenerate} disabled={generating} style={toolBtn({ borderColor: '#99F6E4', color: '#0D9488', background: '#F0FDFA' })}>
            <Sparkles size={14} /> {generating ? 'Generating…' : 'AI Autofill'}
          </button>
          <button type="button" data-testid="share-jd-button" onClick={onShare} style={toolBtn()}><Share2 size={14} /> Share</button>
          <button type="button" data-testid="copy-jd-button" onClick={onCopy} style={toolBtn()}><Copy size={14} /> Copy</button>
          <button type="button" data-testid="download-jd-button" onClick={onDownload} style={toolBtn()}><Download size={14} /> Download</button>
          <button type="button" data-testid="create-job-button" onClick={onCreate} disabled={creating} style={toolBtn({ background: '#0076A3', color: '#fff', borderColor: '#0076A3' })}>
            <Rocket size={14} /> {creating ? 'Creating…' : 'Create Job'}
          </button>
        </div>
      </div>

      {/* Preview surface */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '2rem', background: '#F1EAE1' }} data-testid="jd-preview">
        <div style={{ maxWidth: 720, margin: '0 auto', background: '#fff', border: '1px solid #ECE7DE', borderRadius: 18, padding: '2.25rem 2.25rem 2.5rem', boxShadow: '0 12px 40px rgba(15,23,42,0.06)' }}>
          {empty ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#94A3B8' }}>
              <Briefcase size={40} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
              <div style={{ fontFamily: 'Cabinet Grotesk, sans-serif', fontSize: '1.05rem', fontWeight: 700, color: '#64748B' }}>Your job description will appear here</div>
              <div style={{ fontSize: '0.875rem', marginTop: 6 }}>Fill in the steps on the left, or use <strong style={{ color: '#0D9488' }}>AI Autofill</strong> to start instantly.</div>
            </div>
          ) : (
            <>
              <h1 style={{ fontFamily: 'Cabinet Grotesk, sans-serif', fontSize: '1.85rem', fontWeight: 700, color: '#002A38', lineHeight: 1.15, margin: 0 }}>
                {jd.title || 'Untitled Role'}
              </h1>
              {jd.companyName && <div style={{ fontSize: '0.9375rem', color: '#64748B', marginTop: 6, fontWeight: 600 }}>{jd.companyName}</div>}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '1.1rem 0 1.6rem' }}>
                {[{ Icon: Briefcase, v: jd.jobType }, { Icon: MapPin, v: jd.location }, { Icon: Clock, v: `${jd.experienceYears || 0}+ yrs` }].filter((m) => m.v).map((m) => (
                  <span key={m.v} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 999, background: '#E5EFFF', color: '#0076A3', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' }}>
                    <m.Icon size={13} /> {m.v}
                  </span>
                ))}
                {(() => {
                  const sal = formatSalary({ salary_min: jd.salaryMin, salary_max: jd.salaryMax, salary_currency: jd.salaryCurrency, salary_period: jd.salaryPeriod });
                  return sal ? (
                    <span data-testid="jd-preview-salary" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 999, background: '#CCFBF1', color: '#0F766E', fontSize: '0.75rem', fontWeight: 700 }}>
                      <Wallet size={13} /> {sal}
                    </span>
                  ) : null;
                })()}
              </div>

              {jd.companyContext && (
                <p style={{ fontSize: '0.875rem', color: '#64748B', fontStyle: 'italic', margin: '0 0 1.2rem', lineHeight: 1.6 }}>{jd.companyContext}</p>
              )}
              {jd.overview && (
                <p style={{ fontSize: '0.9375rem', color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: '0 0 1.6rem' }}>{jd.overview}</p>
              )}

              {jd.responsibilities && <Section title="Key Responsibilities"><Bullets text={jd.responsibilities} /></Section>}
              {jd.requiredSkills.length > 0 && <Section title="Required Skills"><Chips items={jd.requiredSkills} color="#0076A3" /></Section>}
              {jd.niceToHave.length > 0 && <Section title="Nice to Have"><Chips items={jd.niceToHave} color="#0D9488" /></Section>}
              {jd.qualifications && <Section title="Preferred Qualifications"><Bullets text={jd.qualifications} /></Section>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
